import { format } from 'date-fns';
import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import {
  Box,
  Button,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { CreateButton } from '../../../components/buttons';
import { SearchField } from '../../../components/inputs';
import { Pagination } from '../../../components/paginations';
import { Spinner } from '../../../components/spinners';
import { Defaults } from '../../../constants/defaults';
import { useGetAnnouncementsByPage } from '../../../lib/announcement/hooks';
import { SortDirection } from '../../../lib/util/helpers';

const AnnouncementList = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
  });
  const { page, search, sort } = query;
  const searchString = search && { search };
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const {
    data: announcements,
    isLoading,
    error,
  } = useGetAnnouncementsByPage({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
  });

  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const setNewPage = (page: number) => setQuery({ page: page - 1 });

  const sortToObj = (sortStr: string | undefined) => {
    const initialValues = { column: '', direction: undefined };
    if (typeof sortStr !== 'string') {
      return initialValues;
    }
    const [column, direction] = sortStr.split(' ');
    if (typeof column !== 'string' || !['asc', 'desc', undefined].includes(direction)) {
      return initialValues;
    }
    return { ...initialValues, column, direction: direction as SortDirection };
  };

  const sortObj = sortToObj(sort ?? '');

  const columns = [
    {
      text: 'Name',
      name: 'announcement.title',
    },
    {
      text: 'Created',
      name: 'announcement.created',
    },
    {
      text: 'Actions',
      name: '',
    },
  ];

  return (
    <Container>
      <FullWidthBar>
        <Typography variant="h5">Announcements</Typography>

        <StyledSearchField
          searchString={search ?? ''}
          onSearch={(search: string) => {
            debouncedSearchFn(search);
          }}
        />

        <CreateButton variant="contained" onClick={() => navigate('/cms/announcements/add')}>
          Add New
        </CreateButton>
      </FullWidthBar>

      <TableContainer component={Paper}>
        <Table aria-labelledby="announcementTable">
          <TableHead>
            <PointerTableRow>
              {columns.map((column) => (
                <TableCell key={column.name}>
                  <TableSortLabel
                    active={sortObj.column === column.name}
                    direction={sortObj.direction}
                    onClick={() => {
                      const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                      setNewSort(column.name, isAsc ? 'desc' : 'asc');
                    }}
                    component="a"
                  >
                    {column.text}
                    {sortObj.column === column.name ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </PointerTableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !error &&
              announcements?.data &&
              announcements?.data.map((row: any, index: number) => {
                return (
                  <StyledTableRow key={index} className={row.deleted ? 'deleted-row' : 'row'}>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.primary">
                        {format(new Date(row.created), 'MM/dd/yyyy hh:mm aaa')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={() => navigate(`/cms/announcements/${row.id}`)}
                        type="button"
                        style={{ textTransform: 'capitalize' }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            {announcements?.data?.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={3} style={{ textAlign: 'center' }}>
                  No announcements records found.
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <FullWidthBar>
        <Typography variant="subtitle1">Total: {announcements?.total}</Typography>
        {!!announcements?.total && !!announcements.limit && (
          <StyledPagination
            current={Number(page) + 1 || 1}
            total={Math.ceil(announcements.total / announcements.limit)}
            onPageChange={(page: number) => setNewPage(page)}
          />
        )}
      </FullWidthBar>
    </Container>
  );
};

export default AnnouncementList;

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const StyledTableRow = styled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});

const StyledSearchField = styled(SearchField)({
  marginLeft: 'auto',
});
